import type { ApolloCache } from '@apollo/client';
import {
  Chip,
  Pagination,
  type Selection,
  type SortDescriptor,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
} from '@heroui/react';
import React, { useCallback, useEffect, useState } from 'react';
import { FiExternalLink, FiInbox } from 'react-icons/fi';
import { LuLayoutGrid, LuLayoutList, LuRefreshCw } from 'react-icons/lu';
import { Link } from 'react-router-dom';
import { ROUTES } from '@/config/routes';
import { Heading } from '@/core/molecules';
import { Button, cn, Spinner, toaster } from '@/core/ui';
import type { QuestionComment } from '@/graphql/gql/graphql';
import {
  CreateQuestionRecordReviewDocument,
  DeleteQuestionRecordReviewsDocument,
  ManageQuestionCommentDocument,
  QuestionCommentsDocument,
} from '@/graphql/gql/graphql';
import { NetworkStatus, useQuery } from '@/libs';
import { gql, useMutation } from '@/libs/apollo/export';
import { useGlobal } from '@/store/global';
import { getDbTypeHashFragment } from '@/utils';
import { formatDateFromUnix } from '@/utils/date';
import { CommentActions } from './comment-actions';
import { CommentMultiActions } from './comment-multi-actions';
import { CommentsGrid } from './comments-grid';

const LIMIT = 50;
const columns: Array<{
  name: string;
  uid: string;
  sortable: boolean;
  width?: number;
}> = [
  { name: 'SR NO.', uid: 'srNo', sortable: false, width: 80 },
  { name: 'ID', uid: 'id', sortable: false, width: 100 },
  { name: 'COMMENT', uid: 'comment', sortable: false },
  { name: 'USER', uid: 'user', sortable: false, width: 150 },
  { name: 'QUESTION ID', uid: 'questionId', sortable: false, width: 120 },
  { name: 'REPLY', uid: 'parentId', sortable: false, width: 100 },
  { name: 'VISIBLE', uid: 'review', sortable: false, width: 100 },
  // { name: 'LIKES/DISLIKES', uid: 'likes', sortable: false, width: 120 },
  { name: 'CREATED AT', uid: 'createdAt', sortable: false, width: 150 },
  { name: 'ACTIONS', uid: 'actions', sortable: false, width: 200 },
];

export const CommentsTable = ({
  filterKey,
  questionIds,
  search,
  // createdAtUnix,
  review,
  productId,
  onRefetch,
  range,
}: {
  filterKey: string;
  questionIds: number[];
  search: string;
  // createdAtUnix: number | undefined;
  review: boolean | null;
  productId: number | null;
  onRefetch?: () => void;
  range: { from: Date | null | number; to: Date | null | number };
}) => {
  const [sortDescriptor, setSortDescriptor] = React.useState<SortDescriptor>({
    column: 'createdAt',
    direction: 'descending',
  });
  const [page, setPage] = useState(1);
  const dbType = useGlobal.use.dbType();
  const [selectedComments, setSelectedComments] = useState<Set<string | number>>(new Set());
  const [viewMode, setViewMode] = useState<'table' | 'grid'>('grid');

  const { data, loading, refetch, networkStatus, client } = useQuery(QuestionCommentsDocument, {
    variables: {
      options: {
        page: page - 1 > 0 ? page : 1,
        take: LIMIT,
        questionIds: questionIds.length > 0 ? questionIds : undefined,
        search: search || undefined,
        from: range.from ?? undefined,
        to: range.to ?? undefined,
        // createdAt: createdAtUnix ?? undefined,
        productId: productId ?? undefined,
        review: review === true ? true : review === false ? false : undefined,
      },
    },
  });

  const isLoading = loading || !data || networkStatus === NetworkStatus.refetch;
  const comments = (data?.restricted?.questionCommentsList?.result ?? []) as QuestionComment[];
  const rows = comments.map((comment, index) => ({
    comment,
    serialNo: (page - 1) * LIMIT + index + 1,
  }));
  const total = data?.restricted?.questionCommentsList?.total ?? 0;

  useEffect(() => {
    setPage(1);
    refetch();
  }, [filterKey]);

  const [manageQuestionCommentMutation, { loading: manageLoading }] = useMutation(
    ManageQuestionCommentDocument,
    {
      update(cache, _, { variables }) {
        if (!variables) return;
        const { ids = [], operation } = variables as { ids: string[]; operation: string };
        if (ids.length === 0 || !operation) return;

        switch (operation) {
          case 'delete':
            ids.forEach((id) => {
              cache.evict({
                id: cache.identify({ __typename: 'QuestionComment', id }),
              });
            });
            break;
          case 'hide':
          case 'unhide': {
            const reviewValue = operation === 'hide';
            ids.forEach((id) => {
              cache.writeFragment({
                id: client.cache.identify({ __typename: 'QuestionComment', id }),
                fragment: gql`
                  fragment UpdatedComment on QuestionComment {
                    review
                  }
                `,
                data: { review: reviewValue },
              });
            });
            break;
          }
          default:
            // Optionally handle unsupported operations
            break;
        }
      },
    },
  );
  const writeCommentRecordReviewId = useCallback(
    (cache: ApolloCache, commentId: string | number, recordReviewId: number | null) => {
      cache.writeFragment({
        id: cache.identify({ __typename: 'QuestionComment', id: String(commentId) }),
        fragment: gql`
          fragment UpdatedCommentRecordReview on QuestionComment {
            recordReviewId
          }
        `,
        data: { recordReviewId },
      });
    },
    [],
  );

  const [createQuestionReview] = useMutation(CreateQuestionRecordReviewDocument, {
    update(cache, { data }) {
      data?.restricted?.createRecordReview?.forEach((review) => {
        if (review?.commentId && review.id) {
          writeCommentRecordReviewId(cache, review.commentId, Number(review.id));
        }
      });
    },
  });
  const [deleteQuestionReviews] = useMutation(DeleteQuestionRecordReviewsDocument);

  const handleMultiAction = useCallback(
    (ids: (string | number)[], operation: string) => {
      if (!ids.length) return;

      if (operation === 'review') {
        const selectedComments = comments.filter(
          (item) => ids.includes(item.id as string | number) && !item.recordReviewId,
        );
        if (!selectedComments.length) {
          toaster.success('Selected comments are already in review');
          return;
        }
        createQuestionReview({
          variables: {
            input: selectedComments.map((c) => ({
              recordId: Number(c.questionId),
              type: 1,
              reason: c.comment || '',
              commentId: c.id ? Number(c.id) : undefined,
            })),
          },
        })
          .then((res) => {
            if (res.data?.restricted?.createRecordReview) {
              const count = selectedComments.length;
              toaster.success(`${count} comment${count > 1 ? 's' : ''} added to review`);
            } else {
              toaster.error('Comments review failed');
            }
          })
          .catch((err) => {
            toaster.error(err.message || 'Comments review failed');
          });
      } else if (operation === 'unreview') {
        const selectedComments = comments.filter(
          (item) => ids.includes(item.id as string | number) && !!item.recordReviewId,
        );
        if (!selectedComments.length) {
          toaster.success('Selected comments are not in review');
          return;
        }
        deleteQuestionReviews({
          variables: { ids: selectedComments.map((c) => String(c.recordReviewId)) },
          update(cache, { data }) {
            if (!data?.restricted?.deleteRecordReviews) return;
            selectedComments.forEach((c) => {
              writeCommentRecordReviewId(cache, c.id as string, null);
            });
          },
        })
          .then((res) => {
            if (res.data?.restricted?.deleteRecordReviews) {
              const count = selectedComments.length;
              toaster.success(`${count} comment${count > 1 ? 's' : ''} removed from review`);
            } else {
              toaster.error('Comments unreview failed');
            }
          })
          .catch((err) => {
            toaster.error(err.message || 'Comments unreview failed');
          });
      } else {
        const idsAsStrings = ids.map((id) => String(id));
        manageQuestionCommentMutation({
          variables: { ids: idsAsStrings, operation },
        })
          .then((res) => {
            if (res.data?.admin?.manageQuestionComment) {
              toaster.success(`${ids.length} comment${ids.length > 1 ? 's' : ''} ${operation} done`);
              setSelectedComments(new Set());
            } else {
              toaster.error(`Comments ${operation} failed`);
            }
          })
          .catch((err) => {
            toaster.error(err.message || `Comments ${operation} failed`);
          });
      }
    },
    [comments, createQuestionReview, deleteQuestionReviews, manageQuestionCommentMutation],
  );

  const handleSelectionChange = (keys: Selection) => {
    if (keys === 'all') {
      setSelectedComments(new Set(comments.map((item) => item.id as string | number)));
    } else {
      setSelectedComments(new Set(Array.from(keys) as (string | number)[]));
    }
  };

  const renderCell = React.useCallback(
    (comment: QuestionComment, columnKey: string, serialNo: number) => {
      if (!comment) return null;
      switch (columnKey) {
        case 'srNo':
          return (
            <div className='flex flex-col'>
              <span className='text-sm font-medium text-default-600'>{serialNo}</span>
            </div>
          );
        case 'id':
          return (
            <div className='flex flex-col'>
              <span className='text-sm font-mono text-default-600'>{comment.id || '-'}</span>
            </div>
          );
        case 'commentId':
          return (
            <div className='flex flex-col'>
              <span className='text-sm font-mono text-default-600'>{comment.id || '-'}</span>
            </div>
          );
        case 'comment':
          return (
            <div
              className={cn('text-sm max-w-sm wrap-break-word rounded-lg p-1 font-mono', {
                'border border-default-200': !comment.review,
                'border bg-warning-50 border-warning-200': comment.review,
              })}
            >
              <code className='text-default-800'>{comment.comment}</code>
            </div>
            // <div className='max-w-md bg-content1 rounded-lg p-2'>
            //   <p className='text-sm text-default-700 line-clamp-2'>{comment.comment || '-'}</p>
            // </div>
          );
        case 'user':
          return (
            <div className='flex flex-col'>
              <span className='text-sm font-medium text-default-700'>
                {comment.user?.displayName || comment.user?.username || 'Anonymous'}
              </span>
              {comment.user?.username && comment.user.username !== comment.user?.displayName && (
                <span className='text-xs text-default-500'>@{comment.user.username}</span>
              )}
            </div>
          );
        case 'questionId':
          return comment.questionId ? (
            <Link
              target='_blank'
              rel='noopener noreferrer'
              to={
                ROUTES.CONTENT.EDIT.QUESTION.replace(':id', String(comment.questionId)) +
                getDbTypeHashFragment('global-state', { dbType })
              }
              className='flex items-center gap-2 text-sm text-default-600 hover:text-default-800 hover:underline'
            >
              <span className='font-mono'>{comment.questionId}</span>
              <FiExternalLink className='size-3.5' />
            </Link>
          ) : (
            <span className='text-sm text-default-400'>-</span>
          );
        case 'parentId':
          return (
            <div className='flex items-center'>
              {comment.parentId ? (
                <Chip className='text-xs' size='sm' color='secondary' variant='flat'>
                  Reply
                </Chip>
              ) : (
                <Chip className='text-xs' size='sm' color='default' variant='flat'>
                  Comment
                </Chip>
              )}
            </div>
          );
        case 'review':
          return (
            <div className='flex items-center'>
              {comment.review ? (
                <Chip className='text-xs' size='sm' color='warning' variant='flat'>
                  NO
                </Chip>
              ) : (
                <Chip className='text-xs' size='sm' color='default' variant='flat'>
                  YES
                </Chip>
              )}
            </div>
          );
        // case 'likes':
        //   return (
        //     <div className='flex items-center gap-3'>
        //       {comment.likes !== null && comment.likes !== undefined && (
        //         <span className='text-sm text-success-500'>👍 {comment.likes}</span>
        //       )}
        //       {comment.dislikes !== null && comment.dislikes !== undefined && (
        //         <span className='text-sm text-danger-500'>👎 {comment.dislikes}</span>
        //       )}
        //       {comment.likes === null && comment.dislikes === null && (
        //         <span className='text-sm text-default-400'>-</span>
        //       )}
        //     </div>
        //   );
        case 'createdAt':
          return comment.createdAt ? (
            <span className='text-sm text-default-600'>
              {formatDateFromUnix(comment.createdAt, 'DD MMM YY | HH:mm')}
            </span>
          ) : (
            <span className='text-sm text-default-400'>-</span>
          );
        case 'actions':
          return <CommentActions comment={comment} onAction={handleMultiAction} />;
        default:
          return <span className='text-sm text-default-400'>-</span>;
      }
    },
    [handleMultiAction, manageLoading, dbType],
  );

  const onRefetchComments = async () => {
    return refetch();
  };

  return (
    <div className='bg-background rounded-lg shadow-md flex flex-col h-full'>
      <Heading
        description={
          <span className='text-sm font-medium text-warning-600'>
            Showing {comments.length} comments on page {page} of {Math.max(1, Math.ceil(total / LIMIT))}{' '}
            (Total: {total})
          </span>
        }
        textClassName='md:text-xl'
        title='Comments'
        className='md:px-6 py-2'
      >
        <div className='flex items-center gap-2'>
          <CommentMultiActions
            selectedComments={Array.from(selectedComments)}
            onAction={handleMultiAction}
            isLoading={manageLoading}
            onClearSelection={() => setSelectedComments(new Set())}
          />
          <Button
            variant={viewMode === 'grid' ? 'solid' : 'bordered'}
            isIconOnly
            size='sm'
            onPress={() => setViewMode('grid')}
            aria-label='Grid view'
          >
            <LuLayoutGrid className='size-4' />
          </Button>
          <Button
            variant={viewMode === 'table' ? 'solid' : 'bordered'}
            isIconOnly
            size='sm'
            onPress={() => setViewMode('table')}
            aria-label='Table view'
          >
            <LuLayoutList className='size-4' />
          </Button>
          <Button
            variant='bordered'
            isIconOnly
            size='sm'
            onPress={() => {
              onRefetchComments();
              if (onRefetch) onRefetch();
            }}
            isDisabled={isLoading}
          >
            <LuRefreshCw className={cn('size-4 text-default-600', { 'animate-spin': isLoading })} />
          </Button>
        </div>
      </Heading>

      {viewMode === 'grid' ? (
        isLoading ? (
          <div className='flex items-center justify-center min-h-[400px]'>
            <Spinner size='lg' />
          </div>
        ) : rows.length === 0 ? (
          <div className='flex flex-col space-y-4 items-center justify-center min-h-[400px]'>
            <FiInbox className='size-16 text-default-400' />
            <p className='text-default-400 text-2xl font-medium'>No comments found</p>
          </div>
        ) : (
          <CommentsGrid
            rows={rows}
            selectedComments={selectedComments}
            onSelect={(id, selected) => {
              setSelectedComments((prev) => {
                const next = new Set(prev);
                if (selected) next.add(id);
                else next.delete(id);
                return next;
              });
            }}
            onAction={handleMultiAction}
          />
        )
      ) : (
        <Table
          isHeaderSticky
          color='default'
          classNames={{
            base: 'max-h-[calc(100vh-400px)] overflow-auto',
            wrapper: 'px-6 scrollbar-thin',
            table: 'min-h-[400px]',
            tr: 'hover:bg-default-100',
            th: [''],
          }}
          isCompact
          shadow='none'
          aria-label='Comments table'
          sortDescriptor={sortDescriptor}
          onSortChange={setSortDescriptor}
          selectionMode='multiple'
          selectedKeys={selectedComments}
          onSelectionChange={handleSelectionChange}
        >
          <TableHeader columns={columns}>
            {(column) => (
              <TableColumn
                className='text-sm'
                allowsSorting={column.sortable}
                key={column.uid}
                align={column.uid === 'actions' ? 'center' : 'start'}
              >
                {column.name}
              </TableColumn>
            )}
          </TableHeader>
          <TableBody
            emptyContent={
              <div className='col-span-full flex flex-col space-y-4 items-center justify-center h-full'>
                <FiInbox className='size-16 text-default-400' />
                <p className='text-default-400 text-2xl font-medium'>No comments found</p>
              </div>
            }
            isLoading={isLoading}
            loadingContent={<Spinner size='lg' />}
            items={rows}
          >
            {(item) => (
              <TableRow key={item.comment.id || String(Math.random())}>
                {(columnKey) => (
                  <TableCell>{renderCell(item.comment, columnKey as string, item.serialNo)}</TableCell>
                )}
              </TableRow>
            )}
          </TableBody>
        </Table>
      )}

      {total > 0 && (
        <div className='p-4 flex justify-between items-center border-t border-default-200'>
          <span className='w-[30%] text-small text-default-400'>
            Page {page} of {Math.max(1, Math.ceil(total / LIMIT))}
          </span>
          <Pagination
            isCompact
            showControls
            color='primary'
            page={page}
            total={Math.max(1, Math.ceil(total / LIMIT))}
            onChange={setPage}
          />
        </div>
      )}
    </div>
  );
};
